import { getATLContextDomain } from '@atlassian/atl-context';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import type { ControllerFull as CurrentUserResponse } from '@atlassian/jira-platform-services-user-current/src/types.tsx';

type UseLoggedInPageParams = {
	/**
	 * Unencoded URL to redirect to after confirming login status
	 */
	pageUrl: string;
};

/**
 * Returns a URL that ensures the user is logged in to the correct account before redirecting to the given `pageUrl`.
 * For example, ensuring that the user is logged in to the same account on WAC before redirecting to the JSFO/JSTO page.
 */
export function useLoggedInPage({ pageUrl }: UseLoggedInPageParams) {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const { data: currentUserData } = useCurrentUser() as CurrentUserResponse;

	const encodedPageUrl = encodeURIComponent(pageUrl);
	// The user email should always be available unless the user is anonymous, in which case they won't see the edition awareness pill anyway
	const encodedUserEmail = encodeURIComponent(currentUserData.user.emailAddress ?? '');

	// This URL will take them through the login flow (if necessary) then redirect them to the given (encoded) `pageUrl`.
	return `https://${getATLContextDomain('id')}/login?continue=${encodedPageUrl}&login_hint=${encodedUserEmail}&prompt=none`;
}

/**
 * Get the start of the UTC day for a given timestamp
 */
export const getStartOfUTCDay = (timestamp: number) => new Date(timestamp).setUTCHours(0, 0, 0, 0);

/**
 * Get the number of days left in the trial
 */
export const getTrialDaysLeft = (
	currentDate: number,
	trialEndTime?: number | null,
): number | undefined =>
	trialEndTime
		? Math.ceil((getStartOfUTCDay(trialEndTime) - currentDate) / (24 * 3600 * 1000))
		: undefined;

/**
 * Check if the site is in grace period
 */
export const isInGracePeriod = (currentDate: number, trialEndTime?: number): boolean => {
	const trialDaysLeft = getTrialDaysLeft(currentDate, trialEndTime);

	// Technically we do not know whether user is in grace period or not, but if user is able to access the Jira with no payment details
	// and is trialEndTime is missing or over we assume that site is in grace period
	return !trialDaysLeft || trialDaysLeft < 0;
};
